import { Fragment, useEffect, useRef } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useTheme } from "../../context/ThemeContext";

const STATUS_COLORS = { in_progress: "#00E5FF", booked: "#60A5FA", confirmed: "#6366f1", assigned: "#0EA5E9" };

const dot = (color, size = 14, pulse = false, label = "") =>
  L.divIcon({
    className: "",
    html: `<div style="position:relative;width:${size}px;height:${size}px;">
      ${pulse ? `<div style="position:absolute;inset:-7px;border-radius:9999px;background:${color};opacity:.3;animation:cc-ping 1.6s ease-out infinite;"></div>` : ""}
      <div style="width:${size}px;height:${size}px;border-radius:9999px;background:${color};border:2px solid rgba(255,255,255,.85);box-shadow:0 0 10px ${color};"></div>
      ${label ? `<div style="position:absolute;top:${size + 3}px;left:50%;transform:translateX(-50%);font:600 9px 'JetBrains Mono',monospace;color:${color};white-space:nowrap;text-shadow:0 1px 3px rgba(0,0,0,.9);">${label}</div>` : ""}
    </div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });

const hqIcon = L.divIcon({
  className: "",
  html: `<div style="position:relative;display:flex;flex-direction:column;align-items:center;">
    <div style="position:absolute;top:-12px;left:50%;transform:translateX(-50%);width:58px;height:58px;border-radius:9999px;background:radial-gradient(circle, rgba(0,229,255,.35), transparent 70%);"></div>
    <div style="width:36px;height:36px;border-radius:10px;background:linear-gradient(160deg,#0e7490,#155e75);border:2px solid #00E5FF;box-shadow:0 0 18px rgba(0,229,255,.85);display:flex;align-items:center;justify-content:center;position:relative;">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#A5F3FC" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M3 21h18"/><path d="M5 21V7l1.5-1.5L8 7l1.5-1.5L11 7V4h2v3l1.5-1.5L16 7l1.5-1.5L19 7v14"/><path d="M10 21v-4a2 2 0 0 1 4 0v4"/></svg>
    </div>
    <div style="margin-top:4px;font:700 9px 'JetBrains Mono',monospace;color:#67E8F9;letter-spacing:.14em;text-shadow:0 1px 4px rgba(0,0,0,.9);background:rgba(6,20,28,.75);border:1px solid rgba(0,229,255,.4);padding:2px 7px;border-radius:5px;white-space:nowrap;">HQ</div>
  </div>`,
  iconSize: [64, 58],
  iconAnchor: [32, 22],
});

function FitOnce({ points }) {
  const map = useMap();
  const done = useRef(false);
  useEffect(() => {
    if (done.current || points.length < 2) return;
    done.current = true;
    map.fitBounds(L.latLngBounds(points), { padding: [45, 45], maxZoom: 11 });
  }, [points, map]);
  return null;
}

export const LiveMap = ({ data }) => {
  const { theme } = useTheme();
  if (!data) return null;

  const points = [[data.hq.lat, data.hq.lng]];
  data.missions.forEach((m) => { points.push([m.pickup.lat, m.pickup.lng]); points.push([m.live.lat, m.live.lng]); });

  const fieldVehicles = data.vehicles.filter((v) => v.state !== "at_hq");
  const fieldCrew = data.employees.filter((e) => e.state !== "at_hq");
  const hqVehicles = data.vehicles.filter((v) => v.state === "at_hq");
  const hqCrew = data.employees.filter((e) => e.state === "at_hq");

  const tiles = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";

  return (
    <div className="relative w-full h-full" data-testid="dispatch-live-map">
      <style>{`
        @keyframes cc-ping { 0% { transform: scale(1); opacity: .35; } 80%, 100% { transform: scale(2.6); opacity: 0; } }
        .leaflet-container { background: ${theme === "dark" ? "#0a0c0e" : "#f1f5f9"}; }
        .leaflet-popup-content-wrapper { border-radius: 12px; }
      `}</style>
      <MapContainer center={[data.hq.lat, data.hq.lng]} zoom={9} scrollWheelZoom className={theme === "dark" ? "map-dark-tiles" : ""} style={{ width: "100%", height: "100%" }}>
        <TileLayer key={theme} url={tiles} attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>' />
        <FitOnce points={points} />

        <Marker position={[data.hq.lat, data.hq.lng]} icon={hqIcon}>
          <Popup>
            <p className="font-semibold text-sm m-0">{data.hq.name}</p>
            <p className="text-xs m-0 mt-1">{hqVehicles.length} vehicle(s) staged · {hqCrew.length} crew on standby</p>
            {hqCrew.length > 0 && <p className="text-xs m-0 mt-1 opacity-70">{hqCrew.map((e) => e.name).join(", ")}</p>}
          </Popup>
        </Marker>

        {data.missions.map((m) => (
          <Fragment key={m.id}>
            {m.status === "in_progress" && (
              <Polyline positions={[[m.pickup.lat, m.pickup.lng], [m.dropoff.lat, m.dropoff.lng]]}
                pathOptions={{ color: "#00E5FF", weight: 2, dashArray: "6 8", opacity: 0.6 }} />
            )}
            <Marker position={[m.live.lat, m.live.lng]}
              icon={dot(STATUS_COLORS[m.status] || "#3b82f6", m.status === "in_progress" ? 16 : 13, m.status === "in_progress", m.mission_number)}>
              <Popup>
                <p className="font-semibold text-sm m-0">{m.mission_number} — {m.customer_name}</p>
                <p className="text-xs m-0 mt-1 capitalize">{m.service_type.replace(/_/g, " ")} · {m.status.replace(/_/g, " ")}</p>
                <p className="text-xs m-0 mt-1">{m.scheduled_date} {m.scheduled_time}</p>
                <p className="text-xs m-0 mt-1 opacity-70">Crew: {m.crew.length ? m.crew.join(", ") : "Unassigned"} · {m.vehicle || "No vehicle"}</p>
              </Popup>
            </Marker>
          </Fragment>
        ))}

        {fieldVehicles.map((v) => (
          <Marker key={v.id} position={[v.lat + 0.006, v.lng]} icon={dot("#f59e0b", 11, false, v.name)}>
            <Popup><p className="text-xs m-0 font-semibold">{v.name}</p><p className="text-xs m-0 opacity-70">{v.type} · {v.plate}</p></Popup>
          </Marker>
        ))}
        {fieldCrew.map((e, i) => (
          <Marker key={e.id}
            position={e.state === "live_gps" ? [e.lat, e.lng] : [e.lat - 0.006 - i * 0.002, e.lng]}
            icon={dot(e.state === "live_gps" ? "#34d399" : "#10b981", e.state === "live_gps" ? 13 : 10, e.state === "live_gps", e.name.split(" ")[0])}>
            <Popup><p className="text-xs m-0 font-semibold">{e.name}</p><p className="text-xs m-0 opacity-70">{e.state === "live_gps" ? "Live GPS position" : e.role}</p></Popup>
          </Marker>
        ))}
      </MapContainer>

      <div className="absolute bottom-3 left-3 z-[1000] rounded-lg border bg-card/90 backdrop-blur px-3 py-2 flex flex-wrap gap-x-4 gap-y-1 text-[10px] font-medium" data-testid="map-legend">
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: "#00E5FF" }} /> En route</span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: "#3b82f6" }} /> Scheduled</span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: "#f59e0b" }} /> Vehicle</span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: "#10b981" }} /> Crew</span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full pulse-dot" style={{ background: "#34d399" }} /> Live GPS</span>
      </div>
    </div>
  );
};
